/**
 * The status field within the metadata response object contains the status of the
 * request, and may contain debugging information to help you troubleshoot why
 * the Street View request is not working.
 */
 export enum StreetViewStatus {
  /**
   * The request was successful.
   */
  Ok = 'OK',

  /**
   * Indicates that no panorama could be found near the provided location. This may
   * occur if a non-existent or invalid panorama ID is given.
   */
  ZeroResults = 'ZERO_RESULTS',

  /**
   * Indicates that the address string provided in the location parameter could not be
   * found. This may occur if a non-existent address is given.
   */
  NotFound = 'NOT_FOUND',

  /**
   * Indicates that you have exceeded your daily quota or per-second quota for this API.
   */
  OverQueryLimit = 'OVER_QUERY_LIMIT',

  /**
   * Indicates that your request was denied. This may occur if you did not authorize
   * your request, or if the Street View Static API is not activated in the Google Cloud
   * Console project containing your API key.
   */
  RequestDenied = 'REQUEST_DENIED',

  /**
   * Generally indicates that the query parameters (address or latlng or components)
   * are missing.
   */
  InvalidRequest = 'INVALID_REQUEST',

  /**
   * Indicates that the request could not be processed due to a server error. This is
   * often a temporary status.
   */
  UnknownError = 'UNKNOWN_ERROR',
}

/**
 * Specifies the output size of the image in pixels.
 */
export interface StreetViewImageSize {
  /**
   * The image width in pixels.
   */
  width: number;

  /**
   * The image length in pixels.
   */
  length: number;
}

/**
 * Limits Street View searches to selected sources.
 */
export enum StreetViewImageSource {
  /**
   * Uses the default sources for Street View; searches are not limited to specific
   * sources.
   */
  Default = 'default',

  /**
   * Limits searches to outdoor collections. Indoor collections are not included in
   * search results.
   *
   * Note that outdoor panoramas may not exist for the specified location.
   */
  Outdoor = 'outdoor'
}

/**
 * The street view image request parameters.
 */
export interface StreetViewImageParameters {
  /**
   * The Street View Static API will snap to the panorama photographed closest to
   * this location.
   *
   * Value can be either a text string (such as Chagrin Falls, OH) or a lat/lng value
   * (40.457375,-80.009353).
   */
  location: string;

  /**
   * A specific panorama ID.
   */
  pano: string;

  /**
   * Specifies the output size of the image in pixels.
   */
  size: StreetViewImageSize;

  /**
   * Indicates the compass heading of the camera.
   */
  heading?: number;

  /**
   * Determines the horizontal field of view of the image.
   *
   * The field of view is expressed in degrees, with a maximum allowed value of 120.
   */
  fov?: number;

  /**
   * Specifies the up or down angle of the camera relative to the Street View vehicle.
   */
  pitch?: number;

  /**
   * Sets a radius, specified in meters, in which to search for a panorama, centered on
   * the given latitude and longitude.
   */
  radius?: number;

  /**
   * Indicates whether the API should return an error code when no image is found
   * (404 NOT FOUND), or in response to an invalid request (400 BAD REQUEST).
   *
   * If set to true, an error message is returned in place of the generic gray image.
   */
  return_error_code?: boolean;

  /**
   * Limits Street View searches to selected sources.
   */
  source?: StreetViewImageSource;
}

export interface StreetViewImageMetaData {
  copyright?: string;
  date?: string;
  location?: google.maps.LatLngLiteral;
  pano_id?: string;
  status: StreetViewStatus;
}
